import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import api from '../../../utils/axiosConfig';
import QRCodeStyling from 'qr-code-styling';

const Referral = ({ supplier }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteData, setInviteData] = useState(null);

  const createStyledQrSvgBlob = async (link) => {
    const qrCode = new QRCodeStyling({
      width: 1024,
      height: 1024,
      type: 'svg',
      data: link,
      margin: 8,
      qrOptions: {
        errorCorrectionLevel: 'Q'
      },
      dotsOptions: {
        type: 'dots',
        color: '#FFFFFF'
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#FFFFFF'
      },
      cornersDotOptions: {
        type: 'dot',
        color: '#FFFFFF'
      },
      backgroundOptions: {
        color: 'transparent'
      }
    });

    const rawSvg = await qrCode.getRawData('svg');
    if (!(rawSvg instanceof Blob)) {
      throw new Error('Unable to generate SVG QR code');
    }

    return rawSvg;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchInviteData = async () => {
      if (!supplier?._id) {
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');
      try {
        const response = await api.get('/api/supplier/invite-link/admin', {
          params: { businessId: supplier._id }
        });
        if (!cancelled) {
          setInviteData(response.data);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.response?.data?.message || 'Unable to load referral details right now.');
          setInviteData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchInviteData();

    return () => {
      cancelled = true;
    };
  }, [supplier?._id]);

  const handleCopyLink = async () => {
    if (!inviteData?.link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteData.link);
      setSuccess('Referral link copied.');
      setError('');
    } catch (copyError) {
      setError('Could not copy the referral link. Please copy it manually.');
    }
  };

  const handleDownloadSvg = async () => {
    if (!inviteData?.link || downloading) {
      return;
    }

    setDownloading(true);
    setError('');
    setSuccess('');
    try {
      const styledSvgBlob = await createStyledQrSvgBlob(inviteData.link);

      const blobUrl = window.URL.createObjectURL(styledSvgBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${(supplier.businessName || 'supplier').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-invite-qr.svg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setSuccess('SVG QR code downloaded.');
    } catch (downloadError) {
      setError(downloadError?.response?.data?.message || 'Could not download SVG right now.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Use this tab to copy the referral link and download the admin SVG QR code for this supplier.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            label="Referral link"
            value={inviteData?.link || ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <TextField
            label="Referral code"
            value={inviteData?.code || ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={handleCopyLink} disabled={!inviteData?.link}>
              Copy link
            </Button>
            <Button variant="contained" onClick={handleDownloadSvg} disabled={!inviteData?.link || downloading}>
              {downloading ? 'Downloading…' : 'Download SVG QR'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Referral;
